import React, { useState, useEffect } from 'react';
import { X, Calendar, Clock, Users } from 'lucide-react';

const MasterclassBanner = ({ onApplyClick, masterclassData, onDismiss }) => {
  const [isAnimating, setIsAnimating] = useState(false);

  // Default masterclass info (fallback)
  const masterclass = masterclassData || {
    title: 'Capital Scaling Masterclass',
    date_formatted: 'Feb 25, 2026',
    time_formatted: '2:30 PM',
    timezone: 'GMT',
    duration_minutes: 45
  };

  useEffect(() => {
    // Trigger animation after mount
    setTimeout(() => setIsAnimating(true), 100);
  }, []);

  const handleDismiss = () => {
    setIsAnimating(false);
    setTimeout(() => {
      sessionStorage.setItem('masterclass_banner_dismissed', 'true');
      // Notify parent component
      if (onDismiss) {
        onDismiss();
      }
    }, 300);
  };

  // Format date for display
  const displayDate = masterclass.date_formatted || 
    (masterclass.date ? new Date(masterclass.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Feb 25, 2026');

  const displayTime = masterclass.time_formatted 
    ? `${masterclass.time_formatted} ${masterclass.timezone || 'GMT'}`
    : `${masterclass.time || '2:30 PM'} ${masterclass.timezone || 'GMT'}`;
    
  const displayDuration = masterclass.duration_minutes ? `${masterclass.duration_minutes} Minutes` : '45 Minutes';

  return (
    <div 
      className={`fixed top-0 left-0 right-0 z-50 bg-[#162959] text-white transition-all duration-500 ${
        isAnimating ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'
      }`}
      style={{ boxShadow: '0 4px 20px rgba(22, 41, 89, 0.3)' }}
      data-testid="masterclass-banner"
    >
      {/* Desktop Layout */}
      <div className="hidden md:block">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            {/* Left: Masterclass Info */}
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <div className="bg-[#337AF7] text-white text-xs font-bold px-2 py-1 rounded uppercase">
                  Live Masterclass
                </div>
                <span className="text-base font-semibold">{masterclass.title || 'Capital Scaling Masterclass'}</span>
              </div>
              
              <div className="h-5 w-px bg-white/30"></div>
              
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1.5 text-white/80">
                  <Calendar className="h-4 w-4" />
                  <span>{displayDate}</span>
                </div>
                <div className="flex items-center gap-1.5 text-white/80">
                  <Clock className="h-4 w-4" />
                  <span>{displayTime}</span>
                </div>
                <div className="flex items-center gap-1.5 text-white/80">
                  <Users className="h-4 w-4" />
                  <span>{displayDuration}</span>
                </div>
              </div>
            </div>

            {/* Right: CTA + Close */}
            <div className="flex items-center gap-3">
              <button
                onClick={onApplyClick}
                data-testid="masterclass-banner-cta"
                className="bg-[#337AF7] text-white px-5 py-2 rounded-lg font-semibold hover:bg-[#2563eb] transition-all duration-300 shadow-lg"
              >
                Apply Now →
              </button>
              
              <button
                onClick={handleDismiss}
                className="p-1.5 hover:bg-white/10 rounded transition-colors"
                aria-label="Dismiss banner"
                data-testid="masterclass-banner-dismiss"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="md:hidden">
        <div className="px-4 py-3">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <div className="bg-[#337AF7] text-white text-xs font-bold px-2 py-0.5 rounded uppercase">
                  Live
                </div>
                <span className="text-sm font-semibold">{masterclass.title || 'Capital Scaling Masterclass'}</span>
              </div>
              
              <div className="flex flex-wrap items-center gap-3 text-xs mt-2 text-white/80">
                <div className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  <span>{displayDate}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  <span>{displayTime}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  <span>{displayDuration}</span>
                </div>
              </div>
            </div>
            
            <button
              onClick={handleDismiss}
              className="p-1 hover:bg-white/10 rounded transition-colors flex-shrink-0"
              aria-label="Dismiss banner"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          <button
            onClick={onApplyClick}
            className="w-full bg-[#337AF7] text-white px-4 py-2 rounded-lg font-semibold hover:bg-[#2563eb] transition-all duration-300 shadow-lg text-sm"
          >
            Apply Now →
          </button>
        </div>
      </div>
    </div>
  );
};

export default MasterclassBanner;
